import { AfterViewInit, Component, HostListener, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subscription, Observable, combineLatest } from 'rxjs';
import { FcpModalService, FcpToastService } from 'fc-ui-components';
import { AutoUnsubscribe, findDataInSnapshot } from 'helpers';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { map, filter, pairwise, first, startWith, distinctUntilChanged, take } from 'rxjs/operators';
import { PreventNavigationService } from 'services/prevent-navigation.service';
import { MediaBreakpoint } from '../../../../components/responsive-wrapper/constants';
import { ModalService, LanguageService } from 'services';
import { StoreSelectorService } from 'app/b2b-order/services/store-selector/store-selector.service';
import { SelectableBranch } from 'app/b2b-order/services/store-selector/types';
import { LogTag } from 'app/b2b-order/services/observable-spy.service';
import { tag } from 'rxjs-spy/operators';
import { StoreService } from 'app/b2b-order/services/store.service';
import { ShoppingCartService } from 'app/b2b-order/services/shopping-cart';

@AutoUnsubscribe
@Component({
    selector: 'fc-choose-stores-modal',
    templateUrl: './stores-modal.component.html',
    styleUrls: ['./stores-modal.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class StoresModalComponent implements OnInit, AfterViewInit {
    @Input() modalId: string;

    public filteredStores: Observable<SelectableBranch[]>;
    public numberOfArticlesInStores: Observable<{ [storeId: string]: number }>;
    public unsavedChanges: BehaviorSubject<SelectableBranch[]> = new BehaviorSubject<SelectableBranch[]>([]);

    public searchStoresForm: FormGroup;

    public warningMessageEmptyUnselectedStoreVisible: Observable<boolean>;
    public errorMessageNoStoreSelectedVisible: Observable<boolean>;

    public displayMode: DisplayMode = DisplayMode.Desktop;
    public subscriptions = new Subscription();
    public combinedStores: Observable<SelectableBranch[]>;

    public hasSelectedStores: Observable<boolean>;
    public isRequired = false;

    constructor(
        private fcpModalService: FcpModalService,
        private storeService: StoreService,
        private formBuilder: FormBuilder,
        // added for create store modal
        private modalService: ModalService,
        private shoppingCartService: ShoppingCartService,
        private storeSelectorService: StoreSelectorService,
        private fcpToastService: FcpToastService,
        private translateService: TranslateService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private preventNavigationService: PreventNavigationService,
        public languageService: LanguageService
    ) {}

    ngOnInit() {
        this.setDisplayMode(window.innerWidth);

        this.storeService.fetchStoresObservable();

        this.searchStoresForm = this.formBuilder.group({
            searchQuery: new FormControl('')
        });

        this.subscriptions.add(
            this.router.events
                .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
                .subscribe(this.displayModalIfNeeded.bind(this))
        );

        this.subscriptions.add(
            this.storeService.stores.pipe(pairwise()).subscribe(([prevStores, stores]) => {
                const prevStoresLength = prevStores.length;
                const newStoresLength = stores.length;
                const newStores: SelectableBranch[] = stores;

                if (prevStoresLength && newStoresLength && newStoresLength > prevStoresLength) {
                    const unsavedChanges = this.unsavedChanges.value;
                    const newStore = newStores[newStores.length - 1];
                    newStore.isSelected = true;
                    unsavedChanges.push(newStore);
                    this.unsavedChanges.next(unsavedChanges);
                }
            })
        );

        // SelectedStores + UnsavedChanges
        this.combinedStores = combineLatest([this.storeSelectorService.selectedStores, this.unsavedChanges]).pipe(
            map(([selectedStores, unsavedChanges]) =>
                _.unionBy(
                    unsavedChanges,
                    selectedStores.map((store) => ({ ...store, isSelected: true })),
                    '_id'
                )
            )
        );
        this.subscriptions.add(this.combinedStores.subscribe());

        // Filter Stores by Search
        this.filteredStores = combineLatest([
            this.combinedStores.pipe(map((stores) => stores.filter((store) => store.isSelected))),
            this.storeService.stores,
            this.searchStoresForm.valueChanges.pipe(startWith(null))
        ]).pipe(
            map(([combinedStores, stores, filter]) => {
                const unfilteredStores = stores.map((store: SelectableBranch) => ({
                    ...store,
                    isSelected: combinedStores.findIndex((selectStore) => store._id === selectStore._id) !== -1
                }));

                const filteredStores = unfilteredStores.filter(
                    (store) =>
                        _.isEmpty(filter) ||
                        this.getStoreTitle(store).toLowerCase().includes(filter.searchQuery.toLowerCase()) ||
                        this.getStoreSubtitle(store).toLowerCase().includes(filter.searchQuery.toLowerCase())
                );

                return filteredStores;
            }),
            tag(LogTag.filteredStores)
        );

        // Calculate number of articles by store
        this.numberOfArticlesInStores = combineLatest([
            this.shoppingCartService.itemCountByStore,
            this.combinedStores
        ]).pipe(
            startWith(),
            distinctUntilChanged(),
            map(([itemsCountByStore, combinedStores]) =>
                combinedStores.reduce((count, store) => {
                    if (store.isSelected) count[store._id] = itemsCountByStore[store._id];
                    return count;
                }, {})
            ),
            tag(LogTag.numberOfArticlesInStores)
        );

        //  validateAtLeastOneStoreIsSelected
        this.errorMessageNoStoreSelectedVisible = this.combinedStores.pipe(
            map((combinedStores) => _.every(combinedStores, { isSelected: false })),
            tag(LogTag.errorMessageNoStoreSelectedVisible)
        );

        // Warning in case of removing store with items
        this.warningMessageEmptyUnselectedStoreVisible = combineLatest([
            this.numberOfArticlesInStores,
            this.combinedStores
        ]).pipe(
            map(([numberOfArticlesInStores, stores]) =>
                _.some(
                    Object.entries(numberOfArticlesInStores),
                    ([storeId, count]) => count > 0 && !stores.find((store) => store._id === storeId)?.isSelected
                )
            ),
            tag(LogTag.warningMessageEmptyUnselectedStoreVisible)
        );

        this.hasSelectedStores = this.storeSelectorService.selectedStores.pipe(map((stores) => Boolean(stores.length)));
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: { target: { innerWidth: number } }) {
        this.setDisplayMode(event.target.innerWidth);
    }

    ngAfterViewInit() {
        this.setDisplayMode(window.innerWidth);
        this.displayModalIfNeeded();
    }

    displayModal(chooseStoreModalId: string) {
        this.subscriptions.add(
            this.storeSelectorService.displayStoresModal(chooseStoreModalId).subscribe(() => {
                this.cancelSave();
            })
        );
    }

    hideModal() {
        this.searchStoresForm?.setValue({ searchQuery: '' });

        if (this.fcpModalService?.isModalOpen(this.modalId)) {
            this.fcpModalService?.close(this.modalId);
        }
    }

    addStore() {
        this.modalService.open('create-store');
    }

    saveStores() {
        this.scrollToErrors();
        this.subscriptions.add(
            combineLatest([this.combinedStores, this.errorMessageNoStoreSelectedVisible])
                .pipe(take(1))
                .subscribe(([combinedStores, errorMessageNoStoreSelectedVisible]) => {
                    if (!errorMessageNoStoreSelectedVisible) {
                        this.storeSelectorService.updateSelectedStores(
                            combinedStores.filter((store) => store.isSelected)
                        );

                        this.unsavedChanges.next([]);
                        this.hideModal();
                        this.fcpToastService.show({
                            text: this.translateService.instant('ORDER.SELECT_STORES_MODAL.STORES_SAVED'),
                            timeout: 6000
                        });
                        this.preventNavigationService.allowNavigation();
                    }
                })
        );
    }

    cancelSave() {
        this.unsavedChanges.next([]);
        this.hideModal();
    }

    selectStore(store: SelectableBranch) {
        const unsavedChanges = this.unsavedChanges.getValue();

        if (!unsavedChanges.includes(store)) {
            unsavedChanges.push(store);
        }

        this.unsavedChanges.next(
            unsavedChanges.map((unsavedStore) => {
                if (unsavedStore._id === store._id) {
                    unsavedStore.isSelected = !store.isSelected;
                }
                return unsavedStore;
            })
        );
    }

    getStoreTitle(store: SelectableBranch): string {
        return store.shortName || store.name || store.address.address1;
    }

    getStoreSubtitle(store: SelectableBranch): string {
        return ` ${store.address.address1} ${store.address.zip} ${store.address.city}`;
    }

    public get DisplayMode() {
        return DisplayMode;
    }

    private setDisplayMode(width: number) {
        if (width < MediaBreakpoint.Desktop) this.displayMode = DisplayMode.Mobile;
        else this.displayMode = DisplayMode.Desktop;
    }

    private scrollToErrors() {
        this.subscriptions.add(
            combineLatest([this.errorMessageNoStoreSelectedVisible, this.warningMessageEmptyUnselectedStoreVisible])
                .pipe(take(1), tag(LogTag.storeModalScrollToError))
                .subscribe(([errorMessageNoStoreSelectedVisible, warningMessageEmptyUnselectedStoreVisible]) => {
                    if (errorMessageNoStoreSelectedVisible || warningMessageEmptyUnselectedStoreVisible) {
                        document
                            .querySelector(`#${this.modalId} .fcp-modal-content`)
                            .scrollTo({ top: 0, behavior: 'smooth' });
                    }
                })
        );
    }

    private displayModalIfNeeded() {
        this.isRequired = ['product', 'shopping-cart'].includes(
            findDataInSnapshot(this.activatedRoute.snapshot, 'selectedMenu')
        );

        this.subscriptions.add(
            combineLatest([this.storeSelectorService.selectedStores, this.storeService.stores])
                .pipe(
                    first(([selectedStores, stores]) => !!selectedStores && stores.length > 0),
                    tag(LogTag.storeModalScrollToError)
                )
                .subscribe(([selectedStores, _]) => {
                    if (!this.fcpModalService.isModalOpen(this.modalId) && this.isRequired && !selectedStores.length) {
                        this.fcpModalService.open(this.modalId, {
                            id: this.modalId,
                            disableClose: true
                        });
                    }
                })
        );
    }
}

enum DisplayMode {
    Desktop = 'desktop',
    Mobile = 'mobile'
}
